import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { Plus, Check, ArrowRight } from "lucide-react";
import { useInquiry } from "@/lib/inquiry-store";
import { SiteLayout } from "@/components/site/SiteLayout";
import { snacksSeed } from "@/lib/snacks-data";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/snacks_/$handle")({
  loader: ({ params }) => {
    const product = snacksSeed.find((p) => p.handle === params.handle);
    if (!product) throw notFound();
    return { product };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: `${loaderData.product.name} – Wholesale QT Foods`,
          description: `Wholesale ${loaderData.product.name} from QT Foods.`,
          image:
            (loaderData.product as any).detail_image_url ||
            loaderData.product.image_url ||
            undefined,
        })
      : [],
  }),
  component: SnackDetailsPage,
});

function SnackDetailsPage() {
  const { product } = Route.useLoaderData();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useInquiry();

  const onAdd = () => {
    add({
      productHandle: product.handle,
      productName: product.name,
      categoryName: product.categories?.name || "",
      imageUrl: product.image_url,
      quantity: qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <SiteLayout
      breadcrumbs={[{ label: "Snacks & More", href: "/snacks" }, { label: product.name }]}
    >
      <section className="mx-auto max-w-5xl px-5 py-12 lg:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          {/* Image */}
          <div className="bg-muted aspect-square w-full overflow-hidden flex items-center justify-center">
            <img
              src={(product as any).detail_image_url || product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
              {product.categories.name}
            </p>
            <h1 className="font-serif text-4xl leading-tight md:text-5xl">{product.name}</h1>

            <div className="mt-8 border-t border-border pt-6">
              <h2 className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Supplier
              </h2>
              <p className="text-lg">{product.specs.region}</p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="hidden items-center border border-border">
                <button
                  className="px-3 py-2 text-lg hover:bg-secondary"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-14 bg-transparent py-2 text-center text-sm focus:outline-none"
                />
                <button
                  className="px-3 py-2 text-lg hover:bg-secondary"
                  onClick={() => setQty((q) => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <button
                onClick={onAdd}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-medium uppercase tracking-[0.15em] text-primary-foreground hover:bg-primary/90"
              >
                {added ? (
                  <>
                    <Check className="h-4 w-4" /> Added to inquiry
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" /> Add to Inquiry
                  </>
                )}
              </button>
            </div>
            <Link
              to="/inquiry"
              className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              Review your inquiry <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
